// src/pages/admin/Settings.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { settingsApi } from "../../api/settingsApi";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Stack,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Avatar,
  useMediaQuery,
  useTheme,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  Save,
  Refresh,
  Settings as SettingsIcon,
  Security,
  Payment,
  Email,
  CloudUpload,
  AccountBalance,
  CheckCircle,
  Close,
  People,
  Edit,
  Payment as PaymentIcon,
  PhoneAndroid,
  AccountBalanceWallet,
  CreditCard,
  PayPal,
  Public,
  LocalAtm,
  QrCode,
  Storefront,
  Savings,
} from "@mui/icons-material";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ✅ Complete Payment Methods Configuration
const PAYMENT_METHODS = [
  { id: 'telebirr', name: 'TeleBirr', icon: '📱', description: 'Pay with TeleBirr mobile money', color: '#00a651' },
  { id: 'mpesa', name: 'M-Pesa', icon: '📱', description: 'Pay with M-Pesa mobile money', color: '#e31e24' },
  { id: 'cbebirr', name: 'CBE Birr', icon: '🏦', description: 'Pay with CBE Birr digital payment', color: '#1a73e8' },
  { id: 'ebirr', name: 'eBirr', icon: '📱', description: 'Pay with eBirr digital payment', color: '#1a73e8' },
  { id: 'coopay', name: 'Coopay', icon: '🏦', description: 'Pay with Coopay eBirr', color: '#2e7d32' },
  { id: 'enat_bank', name: 'Enat Bank', icon: '🏦', description: 'Pay with Enat Bank', color: '#1565c0' },
  { id: 'awashbirr', name: 'Awash Birr', icon: '📱', description: 'Pay with Awash Birr', color: '#ff6f00' },
  { id: 'yaya', name: 'Yaya Wallet', icon: '📱', description: 'Pay with Yaya Wallet', color: '#9c27b0' },
  { id: 'boa_ussd', name: 'BOA USSD', icon: '📱', description: 'Pay with Bank of Abyssinia USSD', color: '#4caf50' },
  { id: 'card', name: 'Credit/Debit Card', icon: '💳', description: 'Pay with Visa, Mastercard, etc.', color: '#1565c0' },
  { id: 'paypal', name: 'PayPal', icon: '🌐', description: 'Pay with PayPal', color: '#003087' },
  { id: 'bank_transfer', name: 'Bank Transfer', icon: '🏦', description: 'Pay via bank transfer', color: '#2e7d32' },
  { id: 'mobile_money', name: 'Mobile Wallet', icon: '📱', description: 'Pay with mobile wallet', color: '#ff6f00' },
];

const AdminSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const [vendors, setVendors] = useState([]);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [commissionRate, setCommissionRate] = useState(0);

  const [settings, setSettings] = useState({
    siteName: "",
    siteLogo: "",
    siteDescription: "",
    maintenanceMode: false,
    currency: "ETB",
    language: "am",
    timezone: "Africa/Addis_Ababa",

    // Chapa Payment Settings
    chapaEnabled: false,
    chapaSecretKey: "",
    chapaEnvironment: "sandbox",
    chapaTestKey: "",
    chapaMethods: {
      telebirr: true,
      mpesa: true,
      cbebirr: true,
      ebirr: true,
      coopay: true,
      enat_bank: true,
      awashbirr: true,
      yaya: true,
      boa_ussd: true,
      card: true,
      paypal: true,
      bank_transfer: true,
      mobile_money: true,
    },
    
    cashOnDeliveryEnabled: false,
    minimumPayout: 500,

    requireVendorApproval: true,
    autoAcceptOrders: false,
    commissionRate: 10,

    smtpHost: "",
    smtpPort: 587,
    smtpSecure: true,
    smtpUser: "",
    smtpPass: "",

    enableTwoFactor: false,
    sessionTimeout: 60,
    debugMode: false,
    cacheEnabled: true,
  });

  useEffect(() => {
    fetchSettings();
    fetchVendors();
  }, []);

  const strictBool = (val) => {
    if (val === true || val === 1 || val === '1' || val === 'true') return true;
    return false;
  };

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsApi.getAdminSettings();

      let settingsData = {};
      if (response?.data?.settings) settingsData = response.data.settings;
      else if (response?.settings) settingsData = response.settings;
      else if (response?.data) settingsData = response.data;
      else if (response) settingsData = response;

      let logo = settingsData.siteLogo || "";
      if (logo.includes("https://api.vtech-solution.comhttps")) {
        logo = logo.replace(
          "https://api.vtech-solution.comhttps//",
          "https://api.vtech-solution.com/"
        );
        logo = logo.replace(
          "https://api.vtech-solution.comhttps://",
          "https://api.vtech-solution.com/"
        );
      }

      setSettings((prev) => ({
        ...prev,
        siteName: settingsData.siteName || "SCITECH Store",
        siteLogo: logo,
        siteDescription: settingsData.siteDescription || "",
        currency: settingsData.currency || "ETB",
        language: settingsData.language || "am",
        timezone: settingsData.timezone || "Africa/Addis_Ababa",

        chapaEnabled: strictBool(settingsData.chapaEnabled) || false,
        chapaSecretKey: settingsData.chapaSecretKey || "",
        chapaEnvironment: settingsData.chapaEnvironment || "sandbox",
        chapaTestKey: settingsData.chapaTestKey || "",
        chapaMethods: settingsData.chapaMethods || {
          telebirr: true,
          mpesa: true,
          cbebirr: true,
          ebirr: true,
          coopay: true,
          enat_bank: true,
          awashbirr: true,
          yaya: true,
          boa_ussd: true,
          card: true,
          paypal: true,
          bank_transfer: true,
          mobile_money: true,
        },
        
        cashOnDeliveryEnabled: strictBool(settingsData.cashOnDeliveryEnabled),
        minimumPayout: settingsData.minimumPayout || 500,

        requireVendorApproval: strictBool(settingsData.requireVendorApproval),
        autoAcceptOrders: strictBool(settingsData.autoAcceptOrders),
        commissionRate: settingsData.commissionRate || 10,

        smtpHost: settingsData.smtpHost || "",
        smtpPort: settingsData.smtpPort || 587,
        smtpSecure: strictBool(settingsData.smtpSecure),
        smtpUser: settingsData.smtpUser || "",
        smtpPass: settingsData.smtpPass || "",

        enableTwoFactor: strictBool(settingsData.enableTwoFactor),
        sessionTimeout: settingsData.sessionTimeout || 60,
        debugMode: strictBool(settingsData.debugMode),
        cacheEnabled: strictBool(settingsData.cacheEnabled),
      }));

      setSuccess("Settings loaded successfully!");
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      console.error("Error fetching settings:", error);
      setError(error?.response?.data?.message || "Failed to load settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await settingsApi.getVendors();
      const vendorsData =
        response.vendors || response.data?.vendors || response.data || [];
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleMethodToggle = (methodId, enabled) => {
    setSettings((prev) => ({
      ...prev,
      chapaMethods: {
        ...prev.chapaMethods,
        [methodId]: enabled,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        siteName: settings.siteName,
        siteLogo: settings.siteLogo,
        siteDescription: settings.siteDescription,
        maintenanceMode: settings.maintenanceMode ? "1" : "0",
        currency: settings.currency,
        language: settings.language,
        timezone: settings.timezone,

        chapaEnabled: settings.chapaEnabled ? "1" : "0",
        chapaSecretKey: settings.chapaSecretKey || "",
        chapaEnvironment: settings.chapaEnvironment || "sandbox",
        chapaTestKey: settings.chapaTestKey || "",
        chapaMethods: settings.chapaMethods,
        
        cashOnDeliveryEnabled: settings.cashOnDeliveryEnabled ? "1" : "0",
        minimumPayout: settings.minimumPayout || 500,

        requireVendorApproval: settings.requireVendorApproval ? "1" : "0",
        autoAcceptOrders: settings.autoAcceptOrders ? "1" : "0",
        commissionRate: settings.commissionRate || 10,

        smtpHost: settings.smtpHost || "",
        smtpPort: settings.smtpPort || 587,
        smtpSecure: settings.smtpSecure ? "1" : "0",
        smtpUser: settings.smtpUser || "",
        smtpPass: settings.smtpPass || "",

        enableTwoFactor: settings.enableTwoFactor ? "1" : "0",
        sessionTimeout: settings.sessionTimeout || 60,
        debugMode: settings.debugMode ? "1" : "0",
        cacheEnabled: settings.cacheEnabled ? "1" : "0",
      };

      const response = await settingsApi.updateAdminSettings(payload);

      if (
        response?.success ||
        response?.status === 200 ||
        response?.status === 201
      ) {
        setSuccess("Settings saved successfully!");
        await fetchSettings();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response?.message || "Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setError(error?.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, WEBP, or SVG)');
      e.target.value = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      e.target.value = null;
      return;
    }

    setLogoUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append("logo", file);

    try {
      const response = await settingsApi.uploadLogo(formData);

      let logoUrl = "";
      if (response?.fullLogoUrl) logoUrl = response.fullLogoUrl;
      else if (response?.logoUrl) logoUrl = response.logoUrl;
      else if (response?.data?.fullLogoUrl) logoUrl = response.data.fullLogoUrl;
      else if (response?.data?.logoUrl) logoUrl = response.data.logoUrl;

      if (logoUrl) {
        if (logoUrl.includes("https://api.vtech-solution.comhttps")) {
          logoUrl = logoUrl.replace(
            "https://api.vtech-solution.comhttps//",
            "https://api.vtech-solution.com/"
          );
          logoUrl = logoUrl.replace(
            "https://api.vtech-solution.comhttps://",
            "https://api.vtech-solution.com/"
          );
        }
        setSettings((prev) => ({ ...prev, siteLogo: logoUrl }));
        setSuccess("Logo uploaded successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError("Failed to get logo URL from response");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      setError(error?.response?.data?.message || "Failed to upload logo. Please try again.");
    } finally {
      setLogoUploading(false);
      e.target.value = null;
    }
  };

  const isMethodEnabled = (methodId) => {
    return settings.chapaMethods?.[methodId] !== false;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 12 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              System Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure platform settings, payment gateways, and vendor commissions
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: "100%", sm: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchSettings}
              disabled={saving}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, val) => setTabValue(val)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
        >
          <Tab label="General" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Chapa Payment" icon={<PaymentIcon />} iconPosition="start" />
          <Tab label="Vendors & Commission" icon={<People />} iconPosition="start" />
          <Tab label="Email" icon={<Email />} iconPosition="start" />
          <Tab label="Security" icon={<Security />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <SettingsIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                General Settings
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Site Name"
                  value={settings.siteName || ""}
                  onChange={(e) => handleChange("siteName", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Site Description"
                  value={settings.siteDescription || ""}
                  onChange={(e) => handleChange("siteDescription", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={settings.currency || "ETB"}
                  onChange={(e) => handleChange("currency", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Language"
                  value={settings.language || "am"}
                  onChange={(e) => handleChange("language", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Avatar
                    src={settings.siteLogo || ""}
                    variant="rounded"
                    sx={{ width: 64, height: 64 }}
                  >
                    {settings.siteName?.[0] || "S"}
                  </Avatar>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={logoUploading}
                    size={isMobile ? "small" : "medium"}
                  >
                    {logoUploading ? "Uploading..." : "Upload Logo"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </Button>
                  {logoUploading && <CircularProgress size={24} />}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.maintenanceMode || false}
                      onChange={(e) => handleChange("maintenanceMode", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Maintenance Mode"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            border: `2px solid ${settings.chapaEnabled ? "#4caf50" : "#ff9800"}`,
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <PaymentIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Chapa Payment Gateway
                </Typography>
                <Chip
                  label={settings.chapaEnvironment === "production" ? "Live" : "Sandbox"}
                  size="small"
                  color={settings.chapaEnvironment === "production" ? "success" : "warning"}
                  sx={{ ml: 1 }}
                />
              </Stack>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.chapaEnabled || false}
                      onChange={(e) => handleChange("chapaEnabled", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>Enable Chapa Payments</span>
                      {settings.chapaEnabled ? (
                        <Chip icon={<CheckCircle />} label="Active" size="small" color="success" />
                      ) : (
                        <Chip icon={<Close />} label="Disabled" size="small" color="error" />
                      )}
                    </Stack>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Chapa Secret Key (Production)"
                  value={settings.chapaSecretKey || ""}
                  onChange={(e) => handleChange("chapaSecretKey", e.target.value)}
                  disabled={!settings.chapaEnabled}
                  size={isMobile ? "small" : "medium"}
                  type="password"
                  helperText="Your Chapa production API secret key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Chapa Test Key"
                  value={settings.chapaTestKey || ""}
                  onChange={(e) => handleChange("chapaTestKey", e.target.value)}
                  disabled={!settings.chapaEnabled}
                  size={isMobile ? "small" : "medium"}
                  type="password"
                  helperText="Your Chapa test API key for sandbox testing"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={settings.chapaEnvironment || "sandbox"}
                    onChange={(e) => handleChange("chapaEnvironment", e.target.value)}
                    disabled={!settings.chapaEnabled}
                    label="Environment"
                  >
                    <MenuItem value="sandbox">Sandbox (Testing)</MenuItem>
                    <MenuItem value="production">Production (Live)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* ✅ Payment Methods Selection - All 13 Methods */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Enable Payment Methods
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Select which payment methods to offer your customers
                </Typography>
                <Grid container spacing={2}>
                  {PAYMENT_METHODS.map((method) => (
                    <Grid item xs={12} sm={6} md={4} key={method.id}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          borderColor: isMethodEnabled(method.id) ? method.color : '#e0e0e0',
                          bgcolor: isMethodEnabled(method.id) ? `${method.color}08` : 'transparent',
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: method.color,
                            bgcolor: `${method.color}12`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: `${method.color}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20,
                            flexShrink: 0,
                          }}
                        >
                          {method.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="600">
                            {method.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {method.description}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isMethodEnabled(method.id)}
                              onChange={(e) => handleMethodToggle(method.id, e.target.checked)}
                              disabled={!settings.chapaEnabled}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: method.color,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  bgcolor: method.color,
                                },
                              }}
                            />
                          }
                          label=""
                          sx={{ ml: 0 }}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>All payments are processed securely through Chapa's infrastructure.</strong>
                    <br />
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      Chapa supports multiple payment methods including TeleBirr, M-Pesa, CBE Birr, eBirr, 
                      Coopay, Enat Bank, Awash Birr, Yaya Wallet, BOA USSD, Credit/Debit Cards, PayPal, 
                      Bank Transfer, and Mobile Wallets.
                    </span>
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Payment color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Additional Payment Options
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.cashOnDeliveryEnabled)}
                      onChange={(e) => handleChange("cashOnDeliveryEnabled", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Cash on Delivery"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Payout (ETB)"
                  value={settings.minimumPayout || 500}
                  onChange={(e) => handleChange("minimumPayout", parseInt(e.target.value) || 0)}
                  size={isMobile ? "small" : "medium"}
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText="Minimum amount vendors can request for payout"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <People color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Vendor Commission Management
                </Typography>
              </Stack>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {vendors.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  No vendors registered yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="center">Commission Rate</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendors.map((vendor) => (
                      <TableRow key={vendor.id}>
                        <TableCell>
                          {vendor.storeName || vendor.name || "Unnamed"}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${vendor.commissionRate || 0}%`}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedVendor(vendor);
                              setCommissionRate(vendor.commissionRate || 0);
                              setCommissionDialogOpen(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Email color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Email Settings
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={settings.smtpHost || ""}
                  onChange={(e) => handleChange("smtpHost", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="SMTP Port"
                  value={settings.smtpPort || 587}
                  onChange={(e) => handleChange("smtpPort", parseInt(e.target.value) || 587)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.smtpSecure)}
                      onChange={(e) => handleChange("smtpSecure", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Use Secure Connection"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Username"
                  value={settings.smtpUser || ""}
                  onChange={(e) => handleChange("smtpUser", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Password"
                  value={settings.smtpPass || ""}
                  onChange={(e) => handleChange("smtpPass", e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Security color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Security Settings
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.requireVendorApproval)}
                      onChange={(e) => handleChange("requireVendorApproval", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Require Vendor Approval"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.autoAcceptOrders)}
                      onChange={(e) => handleChange("autoAcceptOrders", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Auto Accept Orders"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.enableTwoFactor)}
                      onChange={(e) => handleChange("enableTwoFactor", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Enable Two-Factor Authentication"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Session Timeout (minutes)"
                  value={settings.sessionTimeout || 60}
                  onChange={(e) => handleChange("sessionTimeout", parseInt(e.target.value) || 60)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.debugMode)}
                      onChange={(e) => handleChange("debugMode", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Debug Mode"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.cacheEnabled)}
                      onChange={(e) => handleChange("cacheEnabled", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Enable Caching"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <Dialog
        open={commissionDialogOpen}
        onClose={() => setCommissionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Commission</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Commission Rate (%)"
            value={commissionRate}
            onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await settingsApi.updateVendorCommission({
                  vendorId: selectedVendor?.id,
                  commissionRate,
                });
                setCommissionDialogOpen(false);
                setSuccess("Commission updated successfully!");
                setTimeout(() => setSuccess(null), 3000);
                fetchVendors();
              } catch (error) {
                setError("Failed to update commission");
                console.error("Error updating commission:", error);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSettings;